// Recommendation screen — wired to the typed API via TanStack Query.
//
// useRecommendQuery fetches the deterministic stack + LLM prose for the
// session bound in the Zustand store. Loading/error/empty states are
// brand-styled (no third-party spinner). Italian decimal-comma euro
// formatting via @/lib/format ensures "50,30 €/mese" comes from real
// numbers, not hardcoded strings.

import { useEffect, useState, type ReactNode } from "react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { ApiError, type RecommendResponse } from "@incorposano/api-client";

import {
  useActiveSessionQuery,
  useGetRecommendationQuery,
  useLatestCompletedSessionQuery,
  useRecommendMutation,
} from "@/api/queries";
import {
  BrandPriorityDisclosure,
  Button,
  IconButton,
  MimirMark,
  Surface,
  TopBar,
  Wordmark,
} from "@/design-system";
import { useBranding } from "@/lib/branding";
import { formatEur } from "@/lib/format";
import { clinicalChangeFlagLabel, goalLabel } from "@/lib/labels";
import { useScreenTitle } from "@/lib/use-screen-title";
import { cn } from "@/lib/utils";
import { useSession } from "@/stores/session";

// The OpenAPI contract types stack as `dict[str, Any]` (because the backend
// uses a dataclass + asdict() for StackRecommendation). The generated TS
// reflects that as `Record<string, never>`. We know the actual runtime shape
// from backend/src/incorposano/domain/models.py, so we narrow it here.
// TODO(ICSP-followup): model StackRecommendation as a Pydantic schema in the
// backend so OpenAPI carries the full shape and this local type goes away.
interface SkuRec {
  sku_id: string;
  name: string;
  format: string;
  price_eur: number;
  primary_ingredient_id: string;
  daily_dose_mg: number;
  pct_of_typical: number;
  evidence_grade: string;
  onset_weeks: number | null;
  efsa_claim: string | null;
  rationale: string;
  warnings: string[];
}

interface BlockedAlt {
  ingredient_id: string;
  name: string;
  reason: string;
  skus_blocked?: string[];
  alternative?: string | null;
}

interface NarrowedStack {
  user_profile_summary: Record<string, unknown>;
  primary_stack: SkuRec[];
  supporting_stack: SkuRec[];
  blocked_with_alternatives: BlockedAlt[];
  warnings_general: string[];
  // ADR-0015 wave-3-part-1 C-3: goals the user asked about that the
  // tenant's catalog doesn't address (either declared out-of-scope or
  // empirically zero SKU candidates). Surfaced as a behavioural-only
  // section in the prose; the corresponding `[BEHAVIOURAL_ONLY]` blocks
  // get lifted out of the prose and rendered as their own section.
  unsupported_goals?: string[];
  // ICSP-21: two totals shipped by the backend so the customer doesn't
  // see a silent sum of "to start now" + "to consider later". The legacy
  // field stays for backward-compat (now equal to the primary-only figure)
  // — frontend reads the new fields directly when available, falls back
  // to the legacy one for stacks persisted before this commit.
  monthly_cost_primary_eur?: number;
  monthly_cost_full_stack_eur?: number;
  estimated_monthly_cost_eur: number;
  disclaimer: string;
}

function narrowStack(stack: RecommendResponse["stack"]): NarrowedStack {
  return stack as unknown as NarrowedStack;
}

export interface RecommendationProps {
  onSubscribe: () => void;
  onBack: () => void;
}

export function Recommendation({ onSubscribe, onBack }: RecommendationProps) {
  useScreenTitle("La tua raccomandazione");
  const { displayName } = useBranding();
  const sessionId = useSession((s) => s.sessionId);
  const profile = useSession((s) => s.profile);
  const hydrateSession = useSession((s) => s.hydrateSession);
  // If the in-memory store is empty (e.g., user landed here via direct URL or
  // from Account after a hard reload), resume the user's most recent session
  // before showing the empty state.
  const activeQuery = useActiveSessionQuery(!sessionId);
  // ICSP-37: when the active-session query is settled and returned null
  // (every session for this user is `status=complete` after PROFILO
  // PRONTO), fall back to the latest-completed query so /recommendation
  // can hydrate after a fresh signup → intake-complete → logout/login
  // cycle. NoSessionState now renders only when BOTH queries return
  // null AND the store has no sessionId.
  const latestCompletedQuery = useLatestCompletedSessionQuery(
    !sessionId && activeQuery.isFetched && activeQuery.data === null,
  );
  useEffect(() => {
    if (sessionId) return;
    if (activeQuery.data) {
      hydrateSession(activeQuery.data);
      return;
    }
    if (latestCompletedQuery.data) {
      hydrateSession(latestCompletedQuery.data);
    }
  }, [
    sessionId,
    activeQuery.data,
    latestCompletedQuery.data,
    hydrateSession,
  ]);
  // Tri-state for the no-session render: only fire when the store is
  // empty AND both probes have come back null.
  const noSessionFound =
    !sessionId &&
    activeQuery.isFetched &&
    activeQuery.data === null &&
    latestCompletedQuery.isFetched &&
    latestCompletedQuery.data === null;

  // GET-first: a session that already has a persisted recommendation must
  // NOT trigger a regenerate (which re-emails the user). Only fall through
  // to the POST mutation when the GET returns 404 — i.e. the user just
  // completed intake and we need to mint the recommendation for the first
  // time.
  const query = useGetRecommendationQuery(sessionId);
  const generate = useRecommendMutation();
  // ICSP-31 PRIMARY FIX: depend on stable primitives (mutate is stable
  // per React Query; the *.is{Pending,Success,Error} flags are booleans),
  // NOT on the whole `generate` object whose reference is recreated each
  // render. Combined with the new generate.isError early-exit below, this
  // stops the QA Run 3 retry storm (~1000 POSTs observed) — the prior
  // effect re-fired generate.mutate as soon as React swapped the unstable
  // generate dep object, and the existing exits (isPending/isSuccess)
  // didn't catch the isError state. The mutation's own retry policy
  // (belt-and-braces in queries.ts) is a secondary guard; this is the
  // primary cure.
  useEffect(() => {
    if (!sessionId) return;
    if (query.isPending) return;
    if (query.data) return;
    if (!query.isError) return;
    if (generate.isPending || generate.isSuccess || generate.isError) return;
    generate.mutate({ sessionId });
  }, [
    sessionId,
    query.isPending,
    query.data,
    query.isError,
    generate.mutate,
    generate.isPending,
    generate.isSuccess,
    generate.isError,
  ]);

  return (
    <Surface flex>
      <TopBar
        left={
          <div className="flex items-center gap-2.5">
            <IconButton ariaLabel="Indietro" onClick={onBack}>
              <svg
                width="10"
                height="16"
                viewBox="0 0 10 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M8 2L2 8l6 6" />
              </svg>
            </IconButton>
            <Wordmark size={15} displayName={displayName} />
          </div>
        }
        right={
          <IconButton ariaLabel="Salva">
            <svg
              width="16"
              height="18"
              viewBox="0 0 16 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M3 2h10v14l-5-3.5L3 16V2z" />
            </svg>
          </IconButton>
        }
      />

      {(() => {
        // The persisted GET wins; the POST mutation only runs when the GET
        // 404s (first-time generation), so it's the fallback source.
        const data = query.data ?? generate.data ?? null;
        const isLoading =
          query.isPending || (query.isError && generate.isPending);
        const isErrored =
          query.isError && (generate.isError || (!generate.isPending && !generate.data));
        // ADR-0014 B-3: 409 with code=clinical_change_requires_consult is
        // not a transient error — it's a route-layer veto driven by the
        // clinical_change_flags the LLM set. Render the consult message
        // instead of the generic ErrorState. The flag-set survives via
        // the chat session, so the user clears it by replying to MIMIR
        // after they speak to their physician (no retry button — retry
        // would loop on the same 409 until the flag is cleared in chat).
        const consult = parseClinicalChangeConsult(generate.error);
        // ICSP-31: when the route returns 409 with code=intake_not_complete
        // (the user opened the recommendation surface before MIMIR was
        // done with intake), render a dedicated state that directs the
        // user back to chat. Pre-hotfix this fell through to the generic
        // ErrorState with a retry button — the user clicked Retry → 409
        // again → the useEffect above re-fired the POST endlessly. With
        // no retry button on this state and the effect-loop fixed above,
        // the failure is now terminal and recoverable only by completing
        // intake in chat.
        const intakeIncomplete = parseIntakeNotComplete(generate.error);
        const retry = () => {
          if (generate.isError) {
            if (sessionId) generate.mutate({ sessionId });
          } else {
            void query.refetch();
          }
        };
        return (
          <>
            <div className="ics-noscroll flex-1 min-h-0 overflow-y-auto">
              {/* ICSP-37: NoSessionState only after both the active +
                latest-completed probes have come back null. Before
                that we're in flight — LoadingState below covers it. */}
              {noSessionFound && <NoSessionState />}
              {sessionId && isLoading && <LoadingState />}
              {sessionId && !isLoading && consult !== null && (
                <ClinicalChangeConsultState
                  message={consult.message}
                  flags={consult.flags}
                />
              )}
              {sessionId &&
                !isLoading &&
                consult === null &&
                intakeIncomplete && (
                  <IntakeNotCompleteState onBackToChat={onBack} />
                )}
              {sessionId &&
                !isLoading &&
                consult === null &&
                !intakeIncomplete &&
                isErrored && <ErrorState onRetry={retry} />}
              {sessionId && data && consult === null && !intakeIncomplete && (
                <RecommendationScrollBody
                  data={data}
                  profileChips={buildProfileChips(profile)}
                />
              )}
            </div>
            {sessionId &&
              data &&
              consult === null &&
              !intakeIncomplete && (
                <RecommendationCta
                  stack={narrowStack(data.stack)}
                  onSubscribe={onSubscribe}
                />
              )}
          </>
        );
      })()}
    </Surface>
  );
}

// ─── Content ─────────────────────────────────────────────────

function RecommendationScrollBody({
  data,
  profileChips,
}: {
  data: RecommendResponse;
  profileChips: string[];
}) {
  const stack = narrowStack(data.stack);
  const { prose: rawProse } = data;
  // ADR-0016 D-4: two-stage lift. [FARMACISTA_REFERRAL] is the new
  // surface (REGOLA 11); [BEHAVIOURAL_ONLY] stays as back-compat for
  // the transition window — both block types can appear in the same
  // prose during cutover (training-set inertia, same rationale as the
  // _strip_skus_from_behavioural_only retention in recommender_prose.py).
  // Order matters only for the \n{3,} collapse semantics — negligible.
  const { prose: stage1, farmacistaSections } = liftFarmacistaReferral(rawProse);
  const { prose, behaviouralSections } = liftBehaviouralOnly(stage1);

  return (
    <>
      {/* Brand-priority disclosure — visible only when the resolved
        tenant privileges a brand (ADR-0007). For ICS-TEST-01 this
        renders nothing, so the existing UX is unchanged. */}
      <BrandPriorityDisclosure />

      {/* MIMIR's prose */}
      <section className="px-6 pt-5 pb-3.5">
        <div className="flex items-center gap-2 mb-3.5">
          <MimirMark size={22} />
          <span className="font-mono text-[11px] uppercase tracking-[0.8px] text-ink-faint">
            Una nota da mimir
          </span>
        </div>
        <MimirNoteProse text={prose} />
      </section>

      {/* Profile summary */}
      {profileChips.length > 0 && (
        <section className="px-6 pt-2 pb-4.5">
          <div className="flex gap-3 flex-wrap px-4 py-3 bg-paper-muted rounded-xl text-[13px] text-ink-muted">
            {profileChips.map((s, i) => (
              <span key={s} className="flex items-center gap-3">
                {i > 0 && <span className="text-ink-faint">·</span>}
                <span>{s}</span>
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Primary stack */}
      {stack.primary_stack.length > 0 && (
        <section className="px-5 pt-3.5 pb-2">
          <SectionHeader title="Il tuo piano" subtitle="Da iniziare ora" />
          <div className="flex flex-col gap-3">
            {stack.primary_stack.map((sku) => (
              <SkuCard key={sku.sku_id} sku={sku} />
            ))}
          </div>
        </section>
      )}

      {/* Supporting stack */}
      {stack.supporting_stack.length > 0 && (
        <section className="px-5 pt-5 pb-2">
          <SectionHeader title="Da considerare" subtitle="Se vorrai, più avanti" />
          <div className="flex flex-col gap-2.5">
            {stack.supporting_stack.map((sku) => (
              <SkuCard key={sku.sku_id} sku={sku} small />
            ))}
          </div>
        </section>
      )}

      {/* Farmacista-referral sections (ADR-0016) — goals the tenant's
        catalog can't honestly cover get an explicit "talk to your
        pharmacist" surface with brand_category_hints + per-user
        contraindications. Renders BEFORE the legacy behavioural-only
        section during the cutover window. */}
      {farmacistaSections.length > 0 && (
        <section className="px-5 pt-5 pb-2">
          <SectionHeader
            title="Quando parlare col farmacista"
            subtitle="Obiettivi che il tuo farmacista può seguire di persona"
            muted
          />
          <div className="flex flex-col gap-3">
            {farmacistaSections.map((s, i) => (
              <FarmacistaReferralSection key={`fr-${String(i)}`} body={s} />
            ))}
          </div>
        </section>
      )}

      {/* Behavioural-only sections (ADR-0015) — goals the catalog
        doesn't address get an honest "no plan, here are some
        behavioural notes" surface instead of being silently dropped.
        ADR-0016 D-4 keeps this as back-compat for the cutover window;
        retire alongside _strip_skus_from_behavioural_only once we have
        2+ weeks of clean prose outputs in production. */}
      {behaviouralSections.length > 0 && (
        <section className="px-5 pt-5 pb-2">
          <SectionHeader
            title="Senza prodotto, qualche indicazione"
            subtitle="Su obiettivi che il nostro catalogo non copre"
            muted
          />
          <div className="flex flex-col gap-3">
            {behaviouralSections.map((s, i) => (
              <BehaviouralOnlySection key={`bo-${String(i)}`} body={s} />
            ))}
          </div>
        </section>
      )}

      {/* Blocked-with-alternatives */}
      {stack.blocked_with_alternatives.length > 0 && (
        <section className="px-5 pt-5 pb-2">
          <SectionHeader title="Cosa ho escluso, e perché" muted />
          <div
            className="rounded-xl p-4 text-[14px] leading-[1.55] text-ink"
            style={{ background: "#EEF1F3" }}
          >
            {stack.blocked_with_alternatives.map((b, i, arr) => (
              <div
                key={b.ingredient_id || `b-${String(i)}`}
                className={cn("flex gap-2.5", i < arr.length - 1 && "mb-2.5")}
              >
                <Excluded label={b.name} />
                <span className="text-ink-muted">{b.reason}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="px-6 pt-5 pb-7">
        <p className="text-[11.5px] leading-[1.55] text-ink-faint m-0">
          Le indicazioni qui sopra non sostituiscono il parere del medico.
          Se assumi farmaci o sei in gravidanza, parlane con MIMIR prima di
          iniziare il piano.
        </p>
      </section>
    </>
  );
}

function RecommendationCta({
  stack,
  onSubscribe,
}: {
  stack: NarrowedStack;
  onSubscribe: () => void;
}) {
  // ICSP-21: prefer the new explicit fields; fall back to the legacy
  // single total for stacks persisted before the cost-split landed.
  const primaryEur = stack.monthly_cost_primary_eur ?? stack.estimated_monthly_cost_eur;
  const fullEur = stack.monthly_cost_full_stack_eur ?? primaryEur;
  // When the two figures are equal (no supporting items, or backend
  // didn't ship the split) render a single unlabelled line — no point
  // repeating the same number under two labels.
  const showSplit = Math.abs(fullEur - primaryEur) > 0.005;

  return (
    <div
      className="shrink-0 backdrop-blur-md border-t border-[rgba(60,50,40,0.1)] px-5 pt-3.5 pb-3"
      style={{ background: "rgba(250,248,244,0.95)" }}
    >
      {showSplit ? (
        <div className="mb-3 space-y-1.5">
          <TotalLine label="Da iniziare ora" eur={primaryEur} prominent />
          <TotalLine
            label="Stack completo (con i da-considerare)"
            eur={fullEur}
          />
        </div>
      ) : (
        <div className="flex items-baseline justify-between mb-2.5">
          <div>
            <div className="font-mono text-[11.5px] uppercase tracking-[0.4px] text-ink-muted">
              Totale mensile
            </div>
            <div className="flex items-baseline gap-1">
              <span className="font-serif text-[28px] text-ink font-medium">
                {formatEur(primaryEur).replace("€", "").trim()}
              </span>
              <span className="text-[14px] text-ink-muted">€/mese</span>
            </div>
          </div>
          <span className="text-[13px] text-ink-muted border-b border-dotted border-ink-faint cursor-pointer">
            Modifica il piano
          </span>
        </div>
      )}
      <Button variant="primary" full onClick={onSubscribe}>
        Abbonati · spedizione gratuita
      </Button>
    </div>
  );
}

function TotalLine({
  label,
  eur,
  prominent = false,
}: {
  label: string;
  eur: number;
  prominent?: boolean;
}) {
  const amountText = formatEur(eur).replace("€", "").trim();
  return (
    <div className="flex items-baseline justify-between">
      <span
        className={cn(
          "font-mono uppercase tracking-[0.4px]",
          prominent ? "text-[11.5px] text-ink-muted" : "text-[11px] text-ink-faint",
        )}
      >
        {label}
      </span>
      <div className="flex items-baseline gap-1">
        <span
          className={cn(
            "font-serif font-medium",
            prominent ? "text-[24px] text-ink" : "text-[16px] text-ink-muted",
          )}
        >
          {amountText}
        </span>
        <span
          className={cn(
            "text-ink-muted",
            prominent ? "text-[13.5px]" : "text-[12px]",
          )}
        >
          €/mese
        </span>
      </div>
    </div>
  );
}

// ─── States ──────────────────────────────────────────────────

function LoadingState() {
  return (
    <section
      className="px-6 pt-12 pb-12 text-center"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <p className="font-mono text-[11px] uppercase tracking-[0.6px] text-ink-faint mb-3">
        MIMIR sta preparando la tua raccomandazione…
      </p>
      <div
        className="mx-auto h-1.5 w-40 rounded-full bg-paper-muted overflow-hidden"
        aria-hidden="true"
      >
        <div className="h-full w-1/2 rounded-full bg-rame animate-progress-slide" />
      </div>
      <p className="mt-3 text-[12px] text-ink-faint italic">
        Sto valutando interazioni e dosi sicure: ci vogliono pochi secondi.
      </p>
    </section>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <section className="px-6 pt-12 pb-12 text-center" role="alert">
      <p className="font-serif italic text-[18px] text-brick-danger mb-3">
        Qualcosa non ha funzionato.
      </p>
      <p className="text-[13.5px] text-ink-muted mb-6 max-w-[280px] mx-auto">
        Non riesco a recuperare la tua raccomandazione adesso. Puoi
        riprovare fra qualche istante.
      </p>
      <Button variant="ghost" onClick={onRetry}>
        Riprova
      </Button>
    </section>
  );
}

/**
 * ADR-0014 B-3: type-narrow the recommend mutation's error and recover
 * the structured `clinical_change_requires_consult` payload when it's
 * present. Returns null for any other failure shape so the caller can
 * fall through to the generic ErrorState.
 */
function parseClinicalChangeConsult(
  err: unknown,
): { message: string; flags: string[] } | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 409) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(err.body);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const detail = (parsed as { detail?: unknown }).detail;
  if (typeof detail !== "object" || detail === null) return null;
  const d = detail as { code?: unknown; message?: unknown; flags?: unknown };
  if (d.code !== "clinical_change_requires_consult") return null;
  const message = typeof d.message === "string" ? d.message : "";
  const flags = Array.isArray(d.flags)
    ? d.flags.filter((f): f is string => typeof f === "string")
    : [];
  return { message, flags };
}

/**
 * ICSP-31: type-narrow the recommend mutation's error and recognise the
 * `intake_not_complete` 409. Returns true when the backend's structured
 * detail carries `code === "intake_not_complete"`. Tolerant of the
 * legacy string-detail shape (`detail === "intake_not_complete"`) — the
 * route hasn't been migrated to the structured form for this case yet,
 * and a future change there shouldn't break the frontend.
 */
function parseIntakeNotComplete(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status !== 409) return false;
  let parsed: unknown;
  try {
    parsed = JSON.parse(err.body);
  } catch {
    return false;
  }
  if (typeof parsed !== "object" || parsed === null) return false;
  const detail = (parsed as { detail?: unknown }).detail;
  if (detail === "intake_not_complete") return true;
  if (typeof detail === "object" && detail !== null) {
    const code = (detail as { code?: unknown }).code;
    if (code === "intake_not_complete") return true;
  }
  return false;
}

function IntakeNotCompleteState({
  onBackToChat,
}: {
  onBackToChat: () => void;
}) {
  return (
    <section className="px-6 pt-12 pb-12 text-center" role="alert">
      <p className="font-serif italic text-[18px] text-ink mb-3">
        L&apos;intake non è ancora completo.
      </p>
      <p className="text-[14px] leading-[1.6] text-ink-muted mb-6 max-w-[320px] mx-auto">
        Torna in chat e finiamo insieme — bastano un paio di scambi e
        poi sblocchiamo la raccomandazione.
      </p>
      <Button variant="primary" onClick={onBackToChat}>
        Torna alla chat con MIMIR
      </Button>
    </section>
  );
}

function ClinicalChangeConsultState({
  message,
  flags,
}: {
  message: string;
  flags: string[];
}) {
  return (
    <section className="px-6 pt-12 pb-12 text-center" role="alert">
      <p className="font-serif italic text-[18px] text-ink mb-3">
        Aspettiamo un attimo prima di aggiornare il tuo piano.
      </p>
      <p className="text-[14px] leading-[1.6] text-ink-muted mb-5 max-w-[320px] mx-auto">
        {message}
      </p>
      {flags.length > 0 && (
        <ul
          className={cn(
            "list-none p-0 m-0 mb-6 max-w-[320px] mx-auto",
            "text-[12.5px] text-ink-faint",
          )}
        >
          {flags.map((f) => (
            <li key={f} className="my-0.5">
              · {clinicalChangeFlagLabel(f)}
            </li>
          ))}
        </ul>
      )}
      <p className="text-[12.5px] text-ink-faint max-w-[320px] mx-auto">
        Quando hai parlato col medico, torna nella chat e dimmi com'è
        andata — sblocchiamo l'aggiornamento del piano da lì.
      </p>
    </section>
  );
}

function NoSessionState() {
  return (
    <section className="px-6 pt-12 pb-12 text-center">
      <p className="font-serif italic text-[18px] text-ink mb-3">
        Non c&apos;è ancora una conversazione attiva.
      </p>
      <p className="text-[13.5px] text-ink-muted max-w-[280px] mx-auto">
        Torna alla pagina iniziale e parla con MIMIR per qualche minuto.
        Costruirà il tuo piano insieme a te.
      </p>
    </section>
  );
}

// ─── Sub-components ──────────────────────────────────────────

/**
 * Renders MIMIR's recommendation prose as italic serif markdown.
 *
 * Two design calls intentionally diverging from the Chat MimirBubble:
 *   - the OPENING paragraph stays full-italic (the "Sara, dalla nostra
 *     conversazione…" sentence is brand voice — italic carries it)
 *   - subsequent lists / bold runs are NOT italicised: clinical content
 *     (SKU names, dose lines) needs to read as information, not voice
 *
 * Text size 19px (slightly smaller than the previous 21px) so a longer
 * note doesn't dominate the viewport — the screenshot showed the prose
 * eating the entire scrollable area before the SKU list could appear.
 */
function MimirNoteProse({ text }: { text: string }) {
  // Defensive: strip any orphan extraction markers (same pattern as Chat).
  const cleaned = text
    .replace(/\[\[\/?EXTRACTION\]\][\s\S]*?\[\[\/EXTRACTION\]\]/g, "")
    .replace(/\[\[INTAKE_COMPLETE\]\]/g, "")
    .trim();

  return (
    <div
      className={cn(
        "font-serif text-[19px] leading-[1.5] text-ink font-normal",
        // First paragraph italic = brand voice. Remaining paragraphs
        // and lists drop italic for legibility of clinical content.
        "[&_p:first-child]:italic",
        "[&_p]:my-3 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
        "[&_strong]:font-semibold [&_strong]:not-italic",
        "[&_em]:italic",
        // Lists for the SKU bullets MIMIR sometimes generates inline.
        // Tighter list type so it doesn't compete with the prose voice.
        "[&_ul]:my-3 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:marker:text-rame",
        "[&_ul_li]:my-1.5 [&_ul_li]:not-italic [&_ul_li]:text-[16px] [&_ul_li]:leading-[1.5]",
        "[&_ol]:my-3 [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:marker:text-rame",
        "[&_ol_li]:my-1.5 [&_ol_li]:not-italic [&_ol_li]:text-[16px] [&_ol_li]:leading-[1.5]",
        // Headings MIMIR sometimes adds (## Raccomandazione principale, etc.)
        "[&_h2]:font-mono [&_h2]:text-[11px] [&_h2]:uppercase [&_h2]:tracking-[0.8px]",
        "[&_h2]:text-ink-faint [&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:not-italic",
        "[&_h3]:font-mono [&_h3]:text-[11px] [&_h3]:uppercase [&_h3]:tracking-[0.8px]",
        "[&_h3]:text-ink-faint [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:not-italic",
      )}
      style={{ letterSpacing: "-0.1px" }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleaned}</ReactMarkdown>
    </div>
  );
}

function SectionHeader({
  title,
  subtitle,
  muted = false,
}: {
  title: string;
  subtitle?: string;
  muted?: boolean;
}) {
  return (
    <div className="mb-3 px-1">
      <h2
        className={cn(
          "font-serif text-[22px] m-0 font-medium",
          "tracking-[-0.2px]",
          muted ? "text-ink-muted" : "text-ink",
        )}
      >
        {title}
      </h2>
      {subtitle && (
        <div className="font-mono text-[11px] uppercase tracking-[0.6px] text-ink-faint mt-1">
          {subtitle}
        </div>
      )}
    </div>
  );
}

function SkuCard({ sku, small = false }: { sku: SkuRec; small?: boolean }) {
  return (
    <article
      className={cn(
        "bg-paper-card rounded-card shadow-card",
        small ? "p-4" : "p-5",
      )}
    >
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1">
          <h3
            className={cn(
              "font-serif m-0 font-medium tracking-[-0.1px] text-ink",
              small ? "text-[17px]" : "text-[19px]",
            )}
          >
            {sku.name}
          </h3>
          <div className="text-[12.5px] text-ink-muted mt-[3px]">{sku.format}</div>
        </div>
        <div className="font-mono text-[14px] text-ink whitespace-nowrap">
          {formatEur(sku.price_eur)}
        </div>
      </div>

      <p
        className={cn(
          "text-[13.5px] leading-[1.55] text-ink-muted",
          small ? "my-2.5 mb-3" : "my-3 mb-3.5",
        )}
      >
        {sku.rationale}
      </p>

      <div className="flex flex-wrap gap-2 items-center">
        <Badge>Evidenza {sku.evidence_grade}</Badge>
        {sku.onset_weeks !== null && (
          <span className="font-mono text-[11.5px] text-ink-faint">
            ONSET · {sku.onset_weeks} settimane
          </span>
        )}
        {sku.warnings.length > 0 && (
          <SkuWarnings warnings={sku.warnings} skuId={sku.sku_id} />
        )}
      </div>
    </article>
  );
}

/**
 * Per-SKU interaction / safety warnings (ICSP-22). Collapsed view shows
 * the first warning as an amber chip and, when more than one exists, a
 * "+N altre" disclosure button. Expanded view shows the full list inline
 * underneath. Button-driven so it works on touch (no hover-only state).
 */
function SkuWarnings({ warnings, skuId }: { warnings: string[]; skuId: string }) {
  const [expanded, setExpanded] = useState(false);
  const more = warnings.length - 1;
  const listId = `sku-warnings-${skuId}`;
  return (
    <div className="ml-auto flex flex-col items-end gap-1.5 max-w-full">
      <div className="flex items-center gap-1.5 flex-wrap justify-end">
        <span
          className="text-[11.5px] text-amber-warning rounded-md py-[3px] px-2 max-w-[260px]"
          style={{ background: "rgba(196,150,72,0.12)" }}
        >
          {warnings[0]}
        </span>
        {more > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls={listId}
            className={cn(
              "text-[11px] font-mono uppercase tracking-[0.4px]",
              "border border-[rgba(196,150,72,0.35)] bg-transparent rounded-md",
              "py-[2px] px-2 cursor-pointer text-amber-warning",
              "hover:bg-[rgba(196,150,72,0.08)]",
            )}
          >
            {expanded ? "Nascondi" : `+${more} altr${more === 1 ? "a" : "e"}`}
          </button>
        )}
      </div>
      {expanded && more > 0 && (
        <ul
          id={listId}
          className={cn(
            "list-disc pl-5 mt-1 mb-0 self-stretch",
            "text-[12px] leading-[1.55] text-ink-muted",
            "marker:text-amber-warning",
          )}
        >
          {warnings.slice(1).map((w, i) => (
            <li key={`${skuId}-w-${String(i)}`} className="my-0.5">
              {w}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Badge({ children }: { children: ReactNode }) {
  return (
    <span className="font-mono text-[10.5px] uppercase tracking-[0.6px] text-ink-muted bg-paper-muted py-[3px] px-2 rounded">
      {children}
    </span>
  );
}

function Excluded({ label }: { label: string }) {
  return (
    <span className="font-serif text-[14px] text-ink italic whitespace-nowrap shrink-0">
      {label} —
    </span>
  );
}

// ─── Helpers ─────────────────────────────────────────────────

/**
 * ADR-0015 wave-3-part-1 C-3: lift `[BEHAVIOURAL_ONLY]...[/BEHAVIOURAL_ONLY]`
 * blocks out of the prose so the main MimirNote keeps focused on the
 * actual plan; the lifted bodies render in their own section underneath.
 *
 * The backend's prose guardrail already removed SKU mentions from the
 * block bodies — what arrives here is safe to render as-is.
 */
const BEHAVIOURAL_ONLY_RE = /\[BEHAVIOURAL_ONLY\]([\s\S]*?)\[\/BEHAVIOURAL_ONLY\]/g;

function liftBehaviouralOnly(text: string): {
  prose: string;
  behaviouralSections: string[];
} {
  const sections: string[] = [];
  const remaining = text.replace(BEHAVIOURAL_ONLY_RE, (_match, body: string) => {
    sections.push(body.trim());
    return "";
  });
  return { prose: remaining.replace(/\n{3,}/g, "\n\n").trim(), behaviouralSections: sections };
}

function BehaviouralOnlySection({ body }: { body: string }) {
  return (
    <article
      className="bg-paper-card rounded-card shadow-card p-5 text-[14px] leading-[1.55] text-ink"
      style={{ background: "rgba(232,224,210,0.45)" }}
    >
      <div className="font-mono text-[10.5px] uppercase tracking-[0.8px] text-ink-faint mb-2">
        Solo indicazioni · nessun prodotto
      </div>
      <div
        className={cn(
          "[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
          "[&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:marker:text-ink-faint",
          "[&_ul_li]:my-1",
        )}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
      </div>
    </article>
  );
}

/**
 * ADR-0016 D-4: lift `[FARMACISTA_REFERRAL]...[/FARMACISTA_REFERRAL]`
 * blocks out of the prose so the main MimirNote keeps focused on the
 * actual plan; the lifted bodies render in their own section underneath
 * (rendered BEFORE the legacy behavioural-only section).
 *
 * The backend's prose guardrail already removed SKU mentions from the
 * block bodies (CKA #380 two-layer pattern — REGOLA 11 prompt directives
 * + `_strip_skus_from_farmacista_referral` post-processor) — what
 * arrives here is safe to render as-is.
 */
const FARMACISTA_REFERRAL_RE = /\[FARMACISTA_REFERRAL\]([\s\S]*?)\[\/FARMACISTA_REFERRAL\]/g;

function liftFarmacistaReferral(text: string): {
  prose: string;
  farmacistaSections: string[];
} {
  const sections: string[] = [];
  const remaining = text.replace(FARMACISTA_REFERRAL_RE, (_match, body: string) => {
    sections.push(body.trim());
    return "";
  });
  return { prose: remaining.replace(/\n{3,}/g, "\n\n").trim(), farmacistaSections: sections };
}

function FarmacistaReferralSection({ body }: { body: string }) {
  return (
    <article
      className="bg-paper-card rounded-card shadow-card p-5 text-[14px] leading-[1.55] text-ink"
      style={{ background: "rgba(220,210,232,0.45)" }}
    >
      <div className="font-mono text-[10.5px] uppercase tracking-[0.8px] text-ink-faint mb-2">
        Da approfondire in farmacia
      </div>
      <div
        className={cn(
          "[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
          "[&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:marker:text-ink-faint",
          "[&_ul_li]:my-1",
        )}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
      </div>
    </article>
  );
}

function buildProfileChips(
  profile: ReturnType<typeof useSession.getState>["profile"],
): string[] {
  if (!profile) return [];
  const chips: string[] = [];
  if (profile["sex"] === "F") chips.push("Donna");
  else if (profile["sex"] === "M") chips.push("Uomo");
  if (typeof profile["age"] === "number") chips.push(`${String(profile["age"])} anni`);
  const rawGoals = profile["goals"];
  if (Array.isArray(rawGoals) && rawGoals.length > 0) {
    const joined = rawGoals
      .filter((g): g is string => typeof g === "string")
      .map(goalLabel)
      .join(" · ");
    if (joined) chips.push(joined);
  }
  return chips;
}
